/**
 * Cerchar Abrasivity Test — frontend computation mirrors for live preview.
 *
 * SYNC-POINTER: mirrors backend/src/calculation/rockCercharCalculations.ts —
 * keep both in step. The backend recomputes authoritatively on every save; this
 * is preview-only (Result Entry grid). Field names are camelCase to match the FE
 * types (diameter1Mm / diameter2Mm), mirroring the BTS
 * frontend/src/pages/TestWorkflow/rock-bts/computations.ts naming convention.
 *
 * CAI pipeline (confirmed from ASTM CERCHAR Abrasivity.xlsm — Input sheet U26/
 * U28/U30/U32):
 *   perStylusAverage  = AVERAGE(diameter1Mm, diameter2Mm)        (ignore blanks)
 *   avgWearFlatDia    = AVERAGE(all non-null per-stylus averages)
 *   CAIs (Saw Cut CAI)= IF(SawCut, avgWearFlatDia × 10, "-")     — null when broken
 *   CAI (final):
 *     'Saw cut surface' & CAIs <  3.9 → CAIs × 1.14
 *     'Saw cut surface' & CAIs >= 3.9 → 0.99 × CAIs + 0.48
 *     freshly broken (anything else)  → avgWearFlatDia × 10 (no correction)
 *   classification     = first CERCHAR_CLASSIFICATION_BANDS band whose [min,max) contains CAI
 *
 * The CAI constants and the classification bands live in the backend constants
 * (backend/src/constants/rockCerchar.ts). They are duplicated here as local
 * consts ONLY because the FE has no import path to backend modules; any change
 * must be made in both files. No magic numbers inline.
 */

// ── Constants mirrored from backend/src/constants/rockCerchar.ts ─────────────
const CAI_DIAMETER_TO_INDEX_FACTOR = 10;
const CAI_SAW_CUT_THRESHOLD = 3.9;
const CAI_SAW_CUT_FACTOR = 1.14;
const CAI_SAW_CUT_HIGH_SLOPE = 0.99;
const CAI_SAW_CUT_HIGH_INTERCEPT = 0.48;
const SAW_CUT_SURFACE = 'Saw cut surface';

export interface CercharClassificationBand {
  min: number;
  max: number;
  label: string;
}

/** Mirror of CERCHAR_CLASSIFICATION_BANDS (confirmed U32 bands). */
export const CERCHAR_CLASSIFICATION_BANDS: readonly CercharClassificationBand[] = [
  { min: 0, max: 0.5, label: 'Very Low Abrasiveness' },
  { min: 0.5, max: 1.0, label: 'Low Abrasiveness' },
  { min: 1.0, max: 2.0, label: 'Medium Abrasiveness' },
  { min: 2.0, max: 4.0, label: 'High Abrasiveness' },
  { min: 4.0, max: 6.0, label: 'Extreme Abrasiveness' },
  { min: 6.0, max: Infinity, label: 'Quartzite' },
];

/** One stylus row in the Result Entry grid (preview input). */
export interface CercharStylusInput {
  diameter1Mm: number | null;
  diameter2Mm: number | null;
}

/** Live preview of every derived value for one sample (mirrors the backend recompute). */
export interface CercharPreview {
  perStylusAverages: (number | null)[];
  avgWearFlatDiaMm: number | null;
  /** Saw Cut CAI (CAIs) — null for a freshly-broken surface (report renders "-"). */
  caisSawCut: number | null;
  cai: number | null;
  classification: string | null;
}

/** Excel AVERAGE: ignore null/blank; null when all blank (never 0 for "no data"). */
export function average(values: (number | null | undefined)[]): number | null {
  const nums = values.filter((v): v is number => v !== null && v !== undefined && Number.isFinite(v));
  if (nums.length === 0) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

/** AVERAGE(d1, d2) for one stylus; a single present diameter averages to itself. */
export function perStylusAverage(
  d1: number | null | undefined,
  d2: number | null | undefined,
): number | null {
  return average([d1, d2]);
}

/** AVERAGE of the non-null per-stylus averages → average wear-flat diameter. */
export function computeAvgWearFlatDiameter(perStylusAverages: (number | null | undefined)[]): number | null {
  return average(perStylusAverages);
}

/**
 * Saw Cut CAI (U28): `IF(SawCut, avg × 10, "-")`. Returns avg × 10 for a saw-cut
 * surface; **null** for a freshly-broken surface (the saw-cut index is N/A there
 * — the report renders "-"). Null if no average.
 */
export function computeCAIs(
  avgWearFlat: number | null,
  surfaceCondition: string | null | undefined,
): number | null {
  if (avgWearFlat === null) return null;
  if (surfaceCondition !== SAW_CUT_SURFACE) return null;
  return avgWearFlat * CAI_DIAMETER_TO_INDEX_FACTOR;
}

/**
 * Final CAI (U30), conditional on TestSurfaceCondition:
 *   'Saw cut surface' & CAIs <  3.9 → CAIs × 1.14
 *   'Saw cut surface' & CAIs >= 3.9 → 0.99 × CAIs + 0.48
 *   freshly broken (anything else)  → avg × 10 (no correction)
 */
export function computeCAI(
  cais: number | null,
  avgWearFlat: number | null,
  surfaceCondition: string | null | undefined,
): number | null {
  if (surfaceCondition === SAW_CUT_SURFACE) {
    if (cais === null) return null;
    return cais < CAI_SAW_CUT_THRESHOLD
      ? cais * CAI_SAW_CUT_FACTOR
      : CAI_SAW_CUT_HIGH_SLOPE * cais + CAI_SAW_CUT_HIGH_INTERCEPT;
  }
  // Freshly broken (or any non-saw-cut): no correction. avgWearFlat × 10.
  if (avgWearFlat === null) return null;
  return avgWearFlat * CAI_DIAMETER_TO_INDEX_FACTOR;
}

/** Classification band label (half-open [min,max)); null if cai null. */
export function classifyCAI(cai: number | null): string | null {
  if (cai === null) return null;
  for (const band of CERCHAR_CLASSIFICATION_BANDS) {
    if (cai >= band.min && cai < band.max) return band.label;
  }
  return null;
}

/** Live preview of the full CAI pipeline for one sample's 5 styluses. */
export function computeCercharPreview(
  styluses: CercharStylusInput[],
  surfaceCondition: string | null | undefined,
): CercharPreview {
  const perStylusAverages = styluses.map((s) => perStylusAverage(s.diameter1Mm, s.diameter2Mm));
  const avgWearFlatDiaMm = computeAvgWearFlatDiameter(perStylusAverages);
  const caisSawCut = computeCAIs(avgWearFlatDiaMm, surfaceCondition);
  const cai = computeCAI(caisSawCut, avgWearFlatDiaMm, surfaceCondition);
  return {
    perStylusAverages,
    avgWearFlatDiaMm,
    caisSawCut,
    cai,
    classification: classifyCAI(cai),
  };
}
